import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Chip, List, ListItem, ListItemText, Divider, Button } from '@mui/material';
import { Store as MarketIcon, TrendingUp as BuyIcon, TrendingDown as SellIcon, ShoppingCart as FillHoldIcon } from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';
import { useChatContext } from '../../contexts/ChatContext';
import { useConsole } from '../../contexts/ConsoleContext';
import type { Player, MarketItem } from '../../types/gameState';

interface MarketPanelProps {
  playerId: string;
}

export function MarketPanel({ playerId }: MarketPanelProps) {
  const [player, setPlayer] = useState<Player | null>(null);
  const [market, setMarket] = useState<MarketItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sendMessage } = useChatContext();
  const { setSelectedPanel } = useConsole();

  useEffect(() => {
    async function fetchMarket() {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch player first to get station ID
        const playerLogId = logService.api('GET', `/api/players/${playerId}`, {}, 'pending');
        const playerStart = Date.now();
        const playerRes = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}`);
        const playerDuration = Date.now() - playerStart;

        if (!playerRes.ok) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: `HTTP ${playerRes.status}` });
          setError('Failed to load player data');
          setIsLoading(false);
          return;
        }

        const playerResult = await playerRes.json();
        if (!playerResult.success || !playerResult.data) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: 'No player data' });
          setError('No player data available');
          setIsLoading(false);
          return;
        }

        logService.updateLog(playerLogId, { status: 'success', duration: playerDuration });
        setPlayer(playerResult.data);

        // Check if player is docked at a station
        if (!playerResult.data.current_station_id) {
          setIsLoading(false);
          return;
        }

        // Fetch market data for the station
        const marketLogId = logService.api('GET', `/api/stations/${playerResult.data.current_station_id}/market`, {}, 'pending');
        const marketStart = Date.now();
        const marketRes = await fetch(`${DATABASE_SERVICE_URL()}/api/stations/${playerResult.data.current_station_id}/market`);
        const marketDuration = Date.now() - marketStart;

        if (!marketRes.ok) {
          logService.updateLog(marketLogId, { status: 'error', duration: marketDuration, message: `HTTP ${marketRes.status}` });
          setError('Failed to load market data');
          setIsLoading(false);
          return;
        }

        const marketResult = await marketRes.json();
        if (marketResult.success && marketResult.data) {
          logService.updateLog(marketLogId, { status: 'success', duration: marketDuration });
          setMarket(marketResult.data);
        } else {
          logService.updateLog(marketLogId, { status: 'error', duration: marketDuration, message: 'No market data' });
        }
      } catch (err) {
        setError('Failed to load market data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchMarket();
  }, [playerId]);

  if (isLoading) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="rectangular" height={150} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="error" align="center">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  // Player is not docked
  if (!player?.current_station_id) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="overline" sx={{ color: 'warning.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem', display: 'block', mb: 1 }}>
                Market
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                You must be docked at a station to access the market
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  // No market available at this station
  if (market.length === 0) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="overline" sx={{ color: 'warning.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem', display: 'block', mb: 1 }}>
                Market
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                No market available at this station
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  const handleFillHold = () => {
    setSelectedPanel('chat');
    sendMessage('Please fill my cargo hold with profitable commodities');
  };

  return (
    <Box className="panel">
      <Box className="panel-scroll">
        <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'warning.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem' }}>
              Market Prices
            </Typography>
            {market.length > 0 && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<FillHoldIcon />}
                onClick={handleFillHold}
                sx={{ height: 24, fontSize: '0.7rem' }}
              >
                Fill Hold
              </Button>
            )}
          </Box>

          <List sx={{ p: 0 }}>
            {market.map((item: MarketItem, index: number) => (
              <Box key={item.commodity_id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ px: 1, py: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ color: 'warning.main', mr: 1, mt: 0.5 }}>
                    <MarketIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.commodity_name || item.commodity?.name || 'Unknown Commodity'}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          {item.buy_price !== null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <BuyIcon sx={{ fontSize: 14, color: 'success.main' }} />
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'success.main' }}>
                                Buy: ¤{item.buy_price.toLocaleString()}
                              </Typography>
                            </Box>
                          )}
                          {item.sell_price !== null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SellIcon sx={{ fontSize: 14, color: 'error.main' }} />
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'error.main' }}>
                                Sell: ¤{item.sell_price.toLocaleString()}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {item.category && <Chip label={item.category} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                          {item.supply_level && <Chip label={`Supply: ${item.supply_level}`} size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }} />}
                          {item.demand_level && <Chip label={`Demand: ${item.demand_level}`} size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }} />}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
